"use client";

import { Media } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { GallerySectionProps as _GallerySectionProps } from "@/Templates/SectionsProps";
import _ from "lodash";
import { FC, useMemo } from "react";
import HomeThirteenProductSectionGenerated, {
  type HomeThirteenProductSectionGeneratedProps,
} from "./_generated/HomeThirteenProductSection.generated";

interface GallerySectionProps extends _GallerySectionProps {
  showBackgroundImage?: boolean;
}

const TAB_PANE_IDS = [
  "all",
  "pain",
  "antibiotic",
  "cold",
  "diabetes",
  "heart",
  "nutrition",
  "devices",
] as const;

type TabPaneId = (typeof TAB_PANE_IDS)[number];
type GalleryCategoryEntry =
  _GallerySectionProps["dataSource"]["Categories"][number] & {
    tabId: TabPaneId;
    tabKey: string;
    tabContentClassName: string;
    items: _GallerySectionProps["dataSource"]["Items"];
  };
type GalleryItem = _GallerySectionProps["dataSource"]["Items"][number];
type ProductSectionTabPaneItem =
  HomeThirteenProductSectionGeneratedProps["productSectionTabPaneFadeShowActiveFadeInUps"][number];

const Gallery: FC<GallerySectionProps> = ({
  setting,
  dataSource,
  showBackgroundImage: _showBackgroundImage = true,
}) => {
  const t = useT();
  const items = dataSource.Items;

  const visibleCategories = useMemo(
    () => dataSource.Categories.slice(0, TAB_PANE_IDS.length),
    [dataSource.Categories],
  );

  const categoryEntries = useMemo<GalleryCategoryEntry[]>(
    () =>
      visibleCategories.map((category, index) => ({
        ...category,
        tabId: TAB_PANE_IDS[index],
        tabKey: `${TAB_PANE_IDS[index]}-tab`,
        tabContentClassName:
          index === 0 ? "row g-4 justify-content-center" : "row g-4",
        items: items.filter((item) => item.CategoryId === category.Id),
      })),
    [items, visibleCategories],
  );

  const categoryEntryByTabId = useMemo(
    () =>
      new Map<string, GalleryCategoryEntry>(
        categoryEntries.map((entry) => [entry.tabId, entry]),
      ),
    [categoryEntries],
  );

  const buildPaneCardBase = (item: GalleryItem | undefined) => ({
    src: item?.FileUrl ?? "",
    alt:
      item?.Title ?? t("landing.galleryItem.alt", undefined, "Center gallery"),
    link0Text: item?.Title ?? "",
    href: "#",
    link0Href: "#",
    p0Text: item?.Description ?? "",
    h40Text: "",
    span0Text: "",
    span0Span0Text: "",
  });

  const productSectionTabPaneFadeShowActiveFadeInUps = useMemo<
    ProductSectionTabPaneItem[]
  >(
    () =>
      categoryEntries.map((entry, index) => ({
        key: `${entry.tabId}-${entry.Id}`,
        id: entry.tabId,
        ariaLabelledby: entry.tabKey,
        className:
          index === 0
            ? "tab-pane fade show active wow fadeInUp"
            : "tab-pane fade",
        dataWowDuration: index === 0 ? "1s" : undefined,
        div0ClassName:
          index === 0 ? "row g-4 justify-content-center" : "row g-4",
        ...buildPaneCardBase(entry.items[0] ?? items[0]),
      })),
    [buildPaneCardBase, categoryEntries, items, t],
  );

  const productSectionNavs = useMemo(
    () =>
      categoryEntries.map((entry) => ({
        key: entry.tabKey,
        button0Text: entry.Name,
        ariaControls: entry.tabId,
        ariaSelected: entry.tabId === "all",
        className: entry.tabId === "all" ? "nav-link active" : "nav-link",
        dataBsTarget: `#${entry.tabId}`,
        id: entry.tabKey,
        dataBsToggle: "tab",
        liClassName: "nav-item",
      })),
    [categoryEntries],
  );

    const renderGalleryCard = (item: GalleryItem) => (
        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6" key={item.Id}>
            <div className="deals-item-thirteen">
                <div className="delas-overlay">
                    <Media
                        centerFileTypekey={
                            item.FileTypeKey ?? CenterOptions.Enum.CenterFileType.Image.key
                        }
                        src={item.FileUrl}
                        width="100%"
                        height={200}
                        fit="contain"
                        externalVideoProviderKey={item.ExternalVideoProviderKey}
                        className="img-fluid img-1"
                    />
                </div>
                <div className="deals-content">
                    <div className="title">
                        <h3 className="custom-title">{item.Title}</h3>
                        <p
                            className="description"
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {item.Description}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

  if (_.isEmpty(items)) return <></>;

  return (
    <HomeThirteenProductSectionGenerated
      sectionDiv0Div0H20H2Text={setting.Title}
      sectionDiv0Div0Link0LinkVisible={false}
      productSectionNavs={productSectionNavs}
      productSectionTabPaneFadeShowActiveFadeInUps={
        productSectionTabPaneFadeShowActiveFadeInUps
      }
      productSectionTabPaneFadeShowActiveFadeInUpsItemWrapper={(item) => {
        const entry = categoryEntryByTabId.get(item.id);
        if (!entry) {
          return null;
        }

        return (
          <div
            className={item.className}
            data-wow-duration={item.dataWowDuration}
            id={item.id}
            role="tabpanel"
            aria-labelledby={item.ariaLabelledby}
            key={item.key}
          >
            <div className={entry.tabContentClassName}>
              {entry.items.map((galleryItem) => renderGalleryCard(galleryItem))}
            </div>
          </div>
        );
      }}
    />
  );
};

export default Gallery;
